import { useState } from "react";
import { Form, required, useLogin, useNotify } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/simple-crm/root/ConfigurationContext.tsx";

export const LoginPage = (props: { redirectTo?: string }) => {
  const { darkModeLogo, title } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const login = useLogin();
  const notify = useNotify();

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <div className="min-h-screen flex">
      <div className="container relative grid flex-col items-start justify-start sm:max-w-none lg:grid-cols-[7fr_3fr] lg:px-0 min-h-screen">
        <div className="relative hidden h-full flex-col bg-muted p-10 text-white dark:border-r lg:flex">
          <div className="absolute inset-0 bg-zinc-900" />
          <div className="relative z-20 flex flex-col h-full">
            <div className="flex items-center text-xl text-gray-300 font-bold mb-4">
              <img className="h-16 mr-4" src={darkModeLogo} alt={title} />
              Simples assim, sem precisar ser um Salesforce! 🎯✨
            </div>
            
            <div className="mb-8">
              <h2 className="text-3xl font-bold mb-4">
                Sistema de Gestão de Relacionamento
              </h2>
              <p className="text-lg text-gray-300">
                Gerencie seus contatos, empresas e negócios de forma eficiente e profissional
              </p>
            </div>
            
            <div className="flex-1 flex flex-col justify-center space-y-6">
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-white mb-4">
                  🎯 O que é o Simple CRM?
                </h3>
                <p className="text-lg text-gray-300 leading-relaxed">
                  O Simple CRM é um Sistema de Gestão de Relacionamento com Cliente (CRM) moderno e completo, desenvolvido com tecnologias de ponta.
                </p>
              </div>

              <div className="space-y-4">
                <h4 className="text-xl font-bold text-white mb-3">
                  📋 Funcionalidades Principais:
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h5 className="text-lg font-semibold text-blue-300 mb-2">1. Gestão de Contatos</h5>
                    <ul className="text-base text-gray-300 space-y-1 ml-4">
                      <li>• ✅ Cadastro completo de contatos (nome, email, telefone, empresa, etc.)</li>
                      <li>• ✅ Categorização por Tags e Status (Quente, Morno, Frio)</li>
                      <li>• ✅ Histórico de atividades e interações</li>
                      <li>• ✅ Upload de fotos e avatares</li>
                      <li>• ✅ Importação em massa via CSV</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-lg font-semibold text-green-300 mb-2">2. Gestão de Empresas</h5>
                    <ul className="text-base text-gray-300 space-y-1 ml-4">
                      <li>• ✅ Cadastro de empresas com informações completas</li>
                      <li>• ✅ Setores e tamanhos de empresa</li>
                      <li>• ✅ Logos e imagens das empresas</li>
                      <li>• ✅ Endereços e contatos comerciais</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-lg font-semibold text-purple-300 mb-2">3. Pipeline de Vendas (Deals)</h5>
                    <ul className="text-base text-gray-300 space-y-1 ml-4">
                      <li>• ✅ Kanban board para acompanhar negócios</li>
                      <li>• ✅ Estágios de vendas (Oportunidade → Proposta → Negociação)</li>
                      <li>• ✅ Valores em moeda local (R$)</li>
                      <li>• ✅ Probabilidades de fechamento</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="text-lg font-semibold text-orange-300 mb-2">4. Dashboard Inteligente</h5>
                    <ul className="text-base text-gray-300 space-y-1 ml-4">
                      <li>• ✅ Contatos quentes (prioritários)</li>
                      <li>• ✅ Tarefas próximas do vencimento</li>
                      <li>• ✅ Atividade recente</li>
                      <li>• ✅ Gráficos de receita</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xl font-bold text-white mb-3">
                  🎯 Para quem é direcionado?
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h5 className="text-lg font-semibold text-blue-300 mb-2">Empresas de Vendas</h5>
                    <p className="text-base text-gray-300">Equipes comerciais que precisam organizar leads e gerenciar pipelines de vendas</p>
                  </div>
                  
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h5 className="text-lg font-semibold text-green-300 mb-2">Agências e Consultorias</h5>
                    <p className="text-base text-gray-300">Agências de marketing e consultores que trabalham com múltiplos projetos</p>
                  </div>
                  
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <h5 className="text-lg font-semibold text-purple-300 mb-2">Startups e PMEs</h5>
                    <p className="text-base text-gray-300">Empresas em crescimento que precisam de CRM simples e moderno</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-xl font-bold text-white mb-3">
                  💡 Principais Vantagens:
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h5 className="text-lg font-semibold text-yellow-300">Interface Moderna</h5>
                    <p className="text-base text-gray-300">Design limpo, responsivo e modo escuro/claro</p>
                  </div>
                  
                  <div className="space-y-2">
                    <h5 className="text-lg font-semibold text-red-300">Tecnologia Avançada</h5>
                    <p className="text-base text-gray-300">React Admin, Supabase, TypeScript e real-time</p>
                  </div>
                  
                  <div className="space-y-2">
                    <h5 className="text-lg font-semibold text-blue-300">Customizável</h5>
                    <p className="text-base text-gray-300">Campos personalizáveis e integração com APIs</p>
                  </div>
                  
                  <div className="space-y-2">
                    <h5 className="text-lg font-semibold text-green-300">Escalável</h5>
                    <p className="text-base text-gray-300">Múltiplos usuários, permissões e backup automático</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-900 to-purple-900 p-6 rounded-lg">
                <h4 className="text-xl font-bold text-white mb-3">
                  📊 Benefícios para o Negócio:
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">📈</span>
                    <span className="text-gray-300">Aumento de Vendas - Organização melhor dos leads</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">⏰</span>
                    <span className="text-gray-300">Economia de Tempo - Menos tempo procurando informações</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">🎯</span>
                    <span className="text-gray-300">Melhor Acompanhamento - Pipeline visual das vendas</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">📱</span>
                    <span className="text-gray-300">Mobilidade - Responsivo (funciona em mobile/tablet)</span>
                  </div>
                </div>
              </div>

              <div className="text-left">
                <p className="text-lg text-gray-300 font-medium">
                  <strong className="text-white">Em resumo:</strong><br />
                  É um CRM completo, moderno e intuitivo, perfeito para empresas que querem organizar melhor suas vendas e relacionamento com clientes, sem a complexidade de sistemas grandes como Salesforce! 🎯✨
                </p>
              </div>
            </div>
            
            <div className="mt-12 text-sm text-gray-400">
              © 2025 Simple CRM - Todos os direitos reservados
            </div>
          </div>
        </div>
        <div className="lg:p-8 flex items-center justify-center sticky top-0 h-screen">
          <div className="mx-auto flex w-full flex-col justify-center space-y-8 sm:w-[320px]">
            <div className="flex flex-col space-y-2 text-center">
              <h1 className="text-2xl font-semibold tracking-tight">Entrar</h1>
            </div>
            <Form className="space-y-6" onSubmit={handleSubmit}>
              <TextInput
                label="Email"
                source="email"
                type="email"
                validate={required()}
              />
              <TextInput
                label="Senha"
                source="password"
                type="password"
                validate={required()}
              />
              <Button
                type="submit"
                className="cursor-pointer"
                disabled={loading}
              >
                Entrar
              </Button>
            </Form>

            <Link
              to={"/forgot-password"}
              className="text-sm text-center hover:underline"
            >
              Esqueceu sua senha?
            </Link>
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
